import { tagService } from '../utils/api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

/**
 * Hook to get all tags for a school
 * @param {string} schoolId - The school ID
 * @param {string} language - The language code
 * @returns {Object} - Query object with tags data
 */
export const useTags = (schoolId, language) => {
  const { t } = useTranslation();

  return useQuery({
    queryKey: ['tags', schoolId, language],
    queryFn: () => tagService.getTags(schoolId, language),
    enabled: !!schoolId,
    onError: (error) => {
      console.error('Error fetching tags:', error);
      toast.error(t('tags.fetchError', 'Failed to load tags'));
    },
  });
};

/**
 * Hook to create tag
 * @returns {Object} - Mutation object
 */
export const useCreateTag = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ schoolId, tag }) => {
      console.log('[useCreateTag] Mutation function called');
      console.log('[useCreateTag] Received parameters:', { schoolId, tag });
      console.log('[useCreateTag] schoolId type:', typeof schoolId);
      console.log('[useCreateTag] tag type:', typeof tag);
      console.log('[useCreateTag] tag value:', tag);
      
      return tagService.createTag(schoolId, tag);
    },
    onSuccess: (data, variables) => {
      console.log('[useCreateTag] Mutation successful');
      console.log('[useCreateTag] Success data:', data);
      console.log('[useCreateTag] Success variables:', variables);
      toast.success(t('tags.createSuccess', 'Tag created successfully'));
      queryClient.invalidateQueries({ queryKey: ['tags', variables.schoolId] });
    },
    onError: (error, variables) => {
      console.log('[useCreateTag] Mutation failed');
      console.log('[useCreateTag] Error:', error);
      console.log('[useCreateTag] Error variables:', variables);
      console.error('Error creating tag:', error);
      toast.error(t('tags.createError', 'Failed to create tag'));
    },
  });
};

/**
 * Hook to update tag
 * @returns {Object} - Mutation object
 */
export const useUpdateTag = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tagId, tag }) => tagService.updateTag(tagId, tag),
    onSuccess: () => {
      toast.success(t('tags.updateSuccess', 'Tag updated successfully'));
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
    onError: (error) => {
      console.error('Error updating tag:', error);
      toast.error(t('tags.updateError', 'Failed to update tag'));
    },
  });
};

/**
 * Hook to delete tag
 * @returns {Object} - Mutation object
 */
export const useDeleteTag = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tagId) => tagService.deleteTag(tagId),
    onSuccess: (data, tagId, context) => {
      toast.success(t('tags.deleteSuccess', 'Tag deleted successfully'));
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
    onError: (error) => {
      console.error('Error deleting tag:', error);
      toast.error(t('tags.deleteError', 'Failed to delete tag'));
    },
  });
};

/**
 * Hook to update school's tag display setting
 * @returns {Object} - Mutation object
 */
export const useUpdateTagSetting = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ schoolId, showTags }) => tagService.updateTagSetting(schoolId, showTags),
    onSuccess: (data, variables) => {
      toast.success(t('tags.settingUpdateSuccess', 'Tag display setting updated'));

      // Invalidate relevant queries
      console.log('[useUpdateTagSetting] Successful update, invalidating queries', {
        schoolId: variables.schoolId,
        showTags: variables.showTags,
      });

      // Invalidate school settings to reflect the updated show_tags value
      queryClient.invalidateQueries({ 
        queryKey: ['schoolSettings', variables.schoolId] 
      });

      // Also invalidate school info queries that might cache this value
      queryClient.invalidateQueries({ 
        queryKey: ['school', variables.schoolId] 
      });
    },
    onError: (error) => {
      console.error('Error updating tag setting:', error);
      toast.error(t('tags.settingUpdateError', 'Failed to update tag display setting'));
    },
  });
}; 